import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  InvestmentEntityDocumentTypeEnumType,
  UnitStatementPeriodEnumType,
  UnitStatementYearEnumType,
} from '__generated__/graphql';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';
import Select from 'primitives/Select';

import { FormInput, FormPanel } from 'components/FormPanel';

const UPLOAD_INVESTMENT_ENTITY_DOCUMENTS_MUTATION = gql(`
  mutation UploadCustomDocumentInvestmentEntity($investmentEntityId: ID!, $document: Upload!, $documentType: InvestmentEntityDocumentTypeEnumType!, $period: UnitStatementPeriodEnumType!, $year: UnitStatementYearEnumType!, $investorName: String!) {
    uploadCustomDocumentInvestmentEntity(investmentEntityId: $investmentEntityId, document: $document, documentType: $documentType, period: $period, year: $year, investorName: $investorName) {
      id
      type
      name
      description
      documentDate
      createdAt
      updatedAt
    }
  }
`);

const UploadInvestmentEntityDocuments: FC<{
  investmentEntityId: string;
}> = ({ investmentEntityId }) => {
  const [showDialog, toggleDialog] = useState(false);

  const [documentType, setDocumentType] = useState('UNIT_STATEMENT');

  const buttonRef = useRef<HTMLButtonElement>(null);

  const DOC_OPIONS = [
    {
      value: 'UNIT_STATEMENT',
      label: 'Unit Statement',
    },
    {
      value: 'DISTRIBUTION_STATEMENT',
      label: 'Distribution Statement',
    },
    {
      value: 'FORM_64_C',
      label: 'Form 64-C',
    },
  ];

  const [uploadInvestmentEntityDocuments, { loading, error }] = useMutation(
    UPLOAD_INVESTMENT_ENTITY_DOCUMENTS_MUTATION,
    {
      refetchQueries: ['InvestmentEntityDocuments'],
    }
  );

  function handleSubmit(data: any) {
    if (documentType === 'UNIT_STATEMENT') {
      const [period, year] = data.periodAndYear.split(' ');
      // Todo: Make this more robust. This is a hack to get the period and year.
      const periodEnum =
        period === 'H1' ? UnitStatementPeriodEnumType.H1 : UnitStatementPeriodEnumType.H2;
      const yearEnum =
        year === '2024' ? UnitStatementYearEnumType.Year_2024 : UnitStatementYearEnumType.Year_2025;

      uploadInvestmentEntityDocuments({
        variables: {
          investmentEntityId,
          document: data.document,
          documentType: InvestmentEntityDocumentTypeEnumType.UnitStatement,
          period: periodEnum,
          year: yearEnum,
          investorName: data.investorName,
        },
      }).then(() => toggleDialog(false));
    }
  }

  const renderForm = (documentType: string) => {
    switch (documentType) {
      case 'UNIT_STATEMENT':
        return (
          <>
            <FormPanel
              onSubmit={data => {
                handleSubmit(data);
              }}
              onCancel={() => toggleDialog(false)}
              loading={loading}
              error={error}
              submitButtonLabel="Upload Document"
              buttonRef={buttonRef}
            >
              <FormInput
                type="select"
                fieldName="periodAndYear"
                label="Period and Year"
                defaultValue={''}
                options={[
                  {
                    label: 'H2 2024',
                    value: 'H2 2024',
                  },
                  {
                    label: 'H1 2025',
                    value: 'H1 2025',
                  },
                ]}
                validators={{ required: true }}
              />
              <FormInput
                type="text"
                fieldName="investorName"
                label="Investor Name"
                defaultValue=""
                validators={{ required: true }}
              />
              <FormInput
                type="upload"
                fieldName="document"
                label="Document"
                defaultValue=""
                validators={{ required: true }}
              />
            </FormPanel>
          </>
        );
      case 'DISTRIBUTION_STATEMENT':
        return <></>;
      case 'FORM_64_C':
        return <></>;
    }
  };

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Upload Document</Button>

      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        <DialogPanel>
          <DialogTitle>Upload a Document {documentType}</DialogTitle>
          <DialogSubTitle>Upload a document for the investment entity</DialogSubTitle>

          <div className="mb-4">
            <Select
              label="Select Document Type"
              fullWidth
              value={documentType}
              options={DOC_OPIONS}
              onChange={value => {
                setDocumentType(value);
              }}
            />
          </div>
          {renderForm(documentType)}
          <DialogActions>
            <Button
              disabled={loading}
              variant="secondary"
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.cancel();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.submit();
              }}
              loading={loading}
            >
              Upload Document
            </Button>
          </DialogActions>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default UploadInvestmentEntityDocuments;
